import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Divider, Table, TableHead,
  TableRow, TableCell, Switch, FormControlLabel, Badge,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import {
  MY_CEREMONIES_QUERY,
  MY_GUESTS_QUERY,
  CREATE_GUEST_MUTATION,
  UPDATE_GUEST_MUTATION,
  DELETE_GUEST_MUTATION,
  SET_GUEST_ATTENDANCE_MUTATION,
  MY_VIP_ESCORTS_QUERY,
  CREATE_VIP_ESCORT_MUTATION,
  UPDATE_VIP_ESCORT_MUTATION,
  DELETE_VIP_ESCORT_MUTATION,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { useFamily, FAMILY_MANAGER_ROLES } from '../context/FamilyContext';

const RSVP_COLOR = { pending: 'warning', confirmed: 'success', declined: 'error' };
const SIDE_LABEL = { bride: "Bride's", groom: "Groom's", neutral: 'Both' };
// Side is not entered: guests are recorded against the bride/groom family and take its side
const EMPTY_FORM = { name: '', relation: '', phone: '', email: '', isVip: false, dietaryRequirement: 'none', notes: '' };
const FAMILY_SIDE = { bride_family: 'bride', groom_family: 'groom' };
const EMPTY_ESCORT = { ceremonyId: '', escortName: '', escortRole: 'usher', fromLocation: '', toLocation: '', escortTime: '', notes: '' };

export default function GuestListPage() {
  const { user } = useAuth();
  const { family } = useFamily();
  // The family guests are entered against: the selected family (admin/matchmaker), or the logged-in family
  const guestFamily = FAMILY_MANAGER_ROLES.includes(user?.role?.name)
    ? family && { roleName: family.roleName, name: family.displayName }
    : { roleName: user?.role?.name, name: user?.email };
  const familySide = FAMILY_SIDE[guestFamily?.roleName];
  const [dialog, setDialog]     = useState(false);
  const [editGuest, setEdit]    = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [filter, setFilter]     = useState('all');  // all, vip, pending, confirmed, declined
  const [search, setSearch]     = useState('');
  const [error, setError]       = useState('');
  const [attendanceGuest, setAttendanceGuest] = useState(null);
  const [escortDialog, setEscortDialog] = useState(false);
  const [escortGuest, setEscortGuest]   = useState(null);
  const [editEscort, setEditEscort]     = useState(null);
  const [escortForm, setEscortForm]     = useState(EMPTY_ESCORT);

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_GUESTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: escortData, refetch: refetchEscorts } = useQuery(MY_VIP_ESCORTS_QUERY, { fetchPolicy: 'cache-and-network' });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const allGuests  = data?.myGuests || [];

  const filtered = allGuests.filter(g => {
    const matchSearch = !search || g.name.toLowerCase().includes(search.toLowerCase()) || (g.relation || '').toLowerCase().includes(search.toLowerCase());
    if (filter === 'vip')       return g.isVip && matchSearch;
    if (filter === 'pending')   return g.rsvpStatus === 'pending' && matchSearch;
    if (filter === 'confirmed') return g.rsvpStatus === 'confirmed' && matchSearch;
    if (filter === 'declined')  return g.rsvpStatus === 'declined' && matchSearch;
    return matchSearch;
  });

  const summary = { total: allGuests.length, vip: allGuests.filter(g => g.isVip).length, confirmed: allGuests.filter(g => g.rsvpStatus === 'confirmed').length, pending: allGuests.filter(g => g.rsvpStatus === 'pending').length, declined: allGuests.filter(g => g.rsvpStatus === 'declined').length };

  const [createGuest] = useMutation(CREATE_GUEST_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateGuest] = useMutation(UPDATE_GUEST_MUTATION, {
    onCompleted: () => { setDialog(false); setEdit(null); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteGuest] = useMutation(DELETE_GUEST_MUTATION, { onCompleted: () => refetch() });
  const [setAttendance] = useMutation(SET_GUEST_ATTENDANCE_MUTATION, { onCompleted: () => refetch() });
  const [createEscort] = useMutation(CREATE_VIP_ESCORT_MUTATION, {
    onCompleted: () => { setEscortDialog(false); setEscortForm(EMPTY_ESCORT); refetchEscorts(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateEscort] = useMutation(UPDATE_VIP_ESCORT_MUTATION, {
    onCompleted: () => { setEscortDialog(false); setEditEscort(null); setEscortForm(EMPTY_ESCORT); refetchEscorts(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteEscort] = useMutation(DELETE_VIP_ESCORT_MUTATION, { onCompleted: () => refetchEscorts() });

  const allEscorts = escortData?.myVipEscorts || [];

  const openCreate = () => { setEdit(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit   = (g) => { setEdit(g); setForm({ name: g.name, relation: g.relation || '', phone: g.phone || '', email: g.email || '', isVip: g.isVip, dietaryRequirement: g.dietaryRequirement, notes: g.notes || '' }); setDialog(true); };
  const openEscort = (g) => { setEscortGuest(g); setEditEscort(null); setEscortForm(EMPTY_ESCORT); setEscortDialog(true); };
  const openEditEscort = (e) => { setEditEscort(e); setEscortForm({ ceremonyId: e.ceremonyId, escortName: e.escortName || '', escortRole: e.escortRole, fromLocation: e.fromLocation || '', toLocation: e.toLocation || '', escortTime: e.escortTime || '', notes: e.notes || '' }); setEscortDialog(true); };

  const handleSave = () => {
    if (editGuest) updateGuest({ variables: { id: editGuest.id, ...form } });
    else createGuest({ variables: form });
  };
  const handleSaveEscort = () => {
    const vars = { ...escortForm, guestId: escortGuest.id, ceremonyId: escortForm.ceremonyId || null };
    if (editEscort) updateEscort({ variables: { id: editEscort.id, ...escortForm, ceremonyId: escortForm.ceremonyId || null } });
    else createEscort({ variables: vars });
  };

  const handleRsvp = (guest, status) => updateGuest({ variables: { id: guest.id, rsvpStatus: status } });

  const toggleAttendance = (guestId, ceremonyId, current) => setAttendance({ variables: { guestId, ceremonyId, isAttending: !current } });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Guest List</Typography>
          <Typography variant="body2" color="text.secondary">Manage guests, RSVPs, and ceremony attendance.</Typography>
          {familySide && (
            <Typography variant="caption" color="text.secondary">
              Guests are recorded against the {SIDE_LABEL[familySide].toLowerCase()} family ({guestFamily.name}).
            </Typography>
          )}
        </Box>
        <Tooltip title={familySide ? '' : 'Guests can only be added by or for a bride/groom family'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!familySide}>Add Guest</Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: summary.total, color: 'primary' },
          { label: 'VIP',   value: summary.vip,   color: 'secondary' },
          { label: 'Confirmed', value: summary.confirmed, color: 'success' },
          { label: 'Pending',   value: summary.pending,   color: 'warning' },
          { label: 'Declined',  value: summary.declined,  color: 'error' },
        ].map(s => (
          <Grid item xs={6} sm={2.4} key={s.label}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: '12px !important' }}>
                <Typography variant="h4" color={`${s.color}.main`} fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField placeholder="Search guests…" value={search} onChange={e => setSearch(e.target.value)} size="small" sx={{ minWidth: 200 }} />
        {['all','vip','confirmed','pending','declined'].map(f => (
          <Chip key={f} label={f.charAt(0).toUpperCase() + f.slice(1)} onClick={() => setFilter(f)} color={filter === f ? 'primary' : 'default'} variant={filter === f ? 'filled' : 'outlined'} />
        ))}
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {filtered.length === 0 && !loading && <Alert severity="info">No guests match the current filter.</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Relation</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Side</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Dietary</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>RSVP</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Ceremonies</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(g => (
            <TableRow key={g.id} hover>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {g.isVip && <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />}
                  <Typography variant="body2" fontWeight={g.isVip ? 700 : 400}>{g.name}</Typography>
                </Stack>
              </TableCell>
              <TableCell><Typography variant="body2">{g.relation || '—'}</Typography></TableCell>
              <TableCell><Chip label={SIDE_LABEL[g.side]} size="small" variant="outlined" /></TableCell>
              <TableCell><Typography variant="caption">{g.dietaryRequirement === 'none' ? '—' : g.dietaryRequirement}</Typography></TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  <Chip label={g.rsvpStatus} size="small" color={RSVP_COLOR[g.rsvpStatus] || 'default'} onClick={() => handleRsvp(g, g.rsvpStatus === 'confirmed' ? 'pending' : 'confirmed')} />
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {ceremonies.map(c => {
                    const att = g.attendance?.find(a => a.ceremonyId === c.id);
                    const attending = att ? att.isAttending : false;
                    return (
                      <Tooltip key={c.id} title={`${c.name}: ${attending ? 'Attending' : 'Not attending'}`}>
                        <Chip
                          label={c.name.substring(0,8)}
                          size="small"
                          color={attending ? 'primary' : 'default'}
                          variant={attending ? 'filled' : 'outlined'}
                          onClick={() => toggleAttendance(g.id, c.id, attending)}
                          sx={{ fontSize: '0.6rem', height: 20 }}
                        />
                      </Tooltip>
                    );
                  })}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(g)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  {g.isVip && <Tooltip title="Manage VIP Escort"><IconButton size="small" color="secondary" onClick={() => openEscort(g)}><EscalatorWarningIcon fontSize="small" /></IconButton></Tooltip>}
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteGuest({ variables: { id: g.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add / Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editGuest ? 'Edit Guest' : 'Add Guest'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Full Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required autoFocus />
              <TextField label="Relation" value={form.relation} onChange={e => setForm(f => ({ ...f, relation: e.target.value }))} fullWidth placeholder="e.g. Uncle, Friend" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth />
              <TextField label="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Side"
                value={familySide ? `${SIDE_LABEL[familySide]} Side` : '—'}
                helperText="Set from the family this guest is entered against"
                fullWidth
                disabled
              />
              <TextField select label="Dietary" value={form.dietaryRequirement} onChange={e => setForm(f => ({ ...f, dietaryRequirement: e.target.value }))} fullWidth>
                {['none','veg','vegan','jain','diabetic','gluten_free','nut_allergy'].map(v => (
                  <MenuItem key={v} value={v}>{v === 'none' ? 'No restriction' : v.replace(/_/g,' ')}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <FormControlLabel control={<Switch checked={form.isVip} onChange={e => setForm(f => ({ ...f, isVip: e.target.checked }))} />} label="VIP Guest" />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* VIP Escort Summary */}
      {allEscorts.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight={700} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EscalatorWarningIcon color="secondary" /> VIP Escort Assignments
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Guest</TableCell>
                <TableCell>Ceremony</TableCell>
                <TableCell>Escort</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allEscorts.map(e => (
                <TableRow key={e.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={700}>{e.guest?.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{e.ceremony?.name || '—'}</Typography></TableCell>
                  <TableCell>{e.escortName || '—'}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{e.escortRole.replace('_',' ')}</TableCell>
                  <TableCell><Typography variant="caption">{e.fromLocation || '—'} to {e.toLocation || '—'}</Typography></TableCell>
                  <TableCell>{e.escortTime || '—'}</TableCell>
                  <TableCell><Chip label={e.status} size="small" color={e.status === 'completed' ? 'success' : e.status === 'in_progress' ? 'warning' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => { setEscortGuest(e.guest); openEditEscort(e); }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteEscort({ variables: { id: e.id } })}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* VIP Escort Dialog */}
      <Dialog open={escortDialog} onClose={() => setEscortDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editEscort ? 'Edit Escort' : `Assign Escort — ${escortGuest?.name}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select required label="Ceremony" fullWidth value={escortForm.ceremonyId} onChange={e => setEscortForm(f => ({ ...f, ceremonyId: e.target.value }))}>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Escort Name" fullWidth value={escortForm.escortName} onChange={e => setEscortForm(f => ({ ...f, escortName: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField select label="Escort Role" fullWidth value={escortForm.escortRole} onChange={e => setEscortForm(f => ({ ...f, escortRole: e.target.value }))}>
                  {['usher','greeter','coordinator','dedicated_staff'].map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace('_',' ')}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="From Location" fullWidth placeholder="e.g. Main Entrance" value={escortForm.fromLocation} onChange={e => setEscortForm(f => ({ ...f, fromLocation: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="To Location" fullWidth placeholder="e.g. VIP Seating, Stage" value={escortForm.toLocation} onChange={e => setEscortForm(f => ({ ...f, toLocation: e.target.value }))} />
              </Grid>
            </Grid>
            <TextField label="Escort Time" fullWidth placeholder="e.g. 10:30 AM, after arrival" value={escortForm.escortTime} onChange={e => setEscortForm(f => ({ ...f, escortTime: e.target.value }))} />
            {editEscort && (
              <TextField select label="Status" fullWidth value={escortForm.status || 'assigned'} onChange={e => setEscortForm(f => ({ ...f, status: e.target.value }))}>
                {['assigned','in_progress','completed','cancelled'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Notes" fullWidth multiline rows={2} value={escortForm.notes} onChange={e => setEscortForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscortDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEscort} disabled={!escortForm.ceremonyId}>{editEscort ? 'Save' : 'Assign Escort'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
